import React, {
  useState,
  useEffect,
  useMemo,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../services/recruitingApi";
import JobForm from "../components/JobForm";
import {
  JobStatusBadge,
  JobStatusDropdown,
  STATUS_CONFIG,
} from "../components/JobStatusControl";
import TableView from "../components/crm/TableView";
import SearchInput from "../components/SearchInput";
import { ViewSwitcher, ViewMode } from "../components/ViewSwitcher";
import { Pagination } from "../components/Pagination";
import { LoadingState, EmptyState, ErrorState } from "../components/ListStates";
import { FilterSelect } from "../components/FilterSelect";
import { ClearFiltersButton } from "../components/ClearFiltersButton";
import SplitActionButton from "../components/SplitActionButton";
import AppCountBadge from "../components/AppCountBadge";
import { StatusKanbanBoard } from "../components/boards";
import { JobBoardCard } from "../components/boards/JobBoardCard";
import {
  JOB_STATUS_CONFIG,
  JOB_KANBAN_COLUMNS,
  JOB_BOARD_HIDDEN_BY_DEFAULT,
  JOB_ARCHIVE_REASONS,
} from "../components/StatusControl/configs/jobConfig";
import { Chip, Box, Popover, Tooltip } from "@mui/material";
import {
  Users,
  MapPin,
  Briefcase,
  Clock,
  Upload,
  Calendar,
  Search,
  Bot,
  Info,
} from "lucide-react";
import "./JobsList.css";

interface Job {
  id: number;
  title: string;
  department?: string;
  owner_name?: string;
  responsible_sales_name?: string;
  responsible_recruiter_name?: string;
  location?: string;
  seniority?: string;
  status?: string;
  employment_type?: string;
  customer_rate?: number;
  engineer_rate?: number;
  customer_name?: string;
  customer_company_name?: string;
  skills?: string | string[];
  description?: string;
  created_at?: string;
  updated_at?: string;
  application_count?: number;
  application_status_summary?: string;
  most_advanced_app_status?: string;
  similarity_score?: number;
  // Index signature for TableView compatibility
  [key: string]: unknown;
}

interface Customer {
  id: number;
  name?: string;
  company_name?: string;
}

interface JobsResponse {
  data?: Job[];
  unindexed?: Job[];
  total?: number;
}

interface CustomersResponse {
  data?: Customer[];
}

interface SystemConfigResponse {
  data?: {
    value?: string;
  };
}

interface JobFilters {
  status: string;
  department: string;
  seniority: string;
  location: string;
  customer_id: string;
}

// Row type compatible with TableView's RowData
interface TableRowData {
  id: string | number;
  [key: string]: unknown;
}

interface TableColumn {
  id: string;
  label: string;
  render: (value: unknown, row: TableRowData) => React.ReactNode;
}

const JobsList: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [useAISearch, setUseAISearch] = useState<boolean>(false);
  const [filters, setFilters] = useState<JobFilters>({
    status: "",
    department: "",
    seniority: "",
    location: "",
    customer_id: "",
  });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [calendarAnchor, setCalendarAnchor] = useState<{
    el: HTMLElement;
    job: Job;
  } | null>(null);

  // Fetch agency mode setting
  const { data: agencyModeData } = useQuery<SystemConfigResponse>(
    ["system-config", "agency_mode"],
    () =>
      recruitingAPI.getSystemConfig(
        "agency_mode",
      ) as Promise<SystemConfigResponse>,
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      retryDelay: 1000,
      onError: () => {
        // Silently fail - agency mode will default to false
      },
    },
  );

  const isAgencyMode = agencyModeData?.data?.value === "true";

  // Fetch customers if agency mode is enabled
  const { data: customersData } = useQuery<CustomersResponse>(
    ["customers"],
    () =>
      recruitingAPI.getCustomers({
        status: "active",
      }) as Promise<CustomersResponse>,
    {
      enabled: isAgencyMode,
      staleTime: 2 * 60 * 1000,
    },
  );

  const customers = customersData?.data || [];

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch jobs
  const {
    data: jobsData,
    isLoading,
    error,
  } = useQuery<{ jobs: Job[]; total: number }>(
    ["jobs", debouncedSearch, filters, currentPage, useAISearch],
    async () => {
      if (useAISearch && debouncedSearch.trim()) {
        const response = (await recruitingAPI.semanticSearchJobs(
          debouncedSearch,
          20,
        )) as JobsResponse;
        const semanticJobs = response.data || [];
        const unindexedJobs = (response.unindexed || []).map((j: Job) => ({
          ...j,
          _unindexed: true,
        }));
        return {
          jobs: [...semanticJobs, ...unindexedJobs],
          total: semanticJobs.length + unindexedJobs.length,
        };
      } else {
        const params: Record<string, string | number> = {
          q: debouncedSearch,
          page: currentPage,
          pageSize: 20,
          ...filters,
        };
        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (
            params[key] === "" ||
            params[key] === null ||
            params[key] === undefined
          ) {
            delete params[key];
          }
        });
        const response = (await recruitingAPI.getJobs(params)) as JobsResponse;
        return {
          jobs: response.data || [],
          total: response.total || response.data?.length || 0,
        };
      }
    },
    { keepPreviousData: true, enabled: viewMode !== "board" },
  );

  const jobs = jobsData?.jobs || [];

  // Delete mutation
  const deleteMutation = useMutation(
    async (id: number) => await recruitingAPI.deleteJob(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs");
      },
    },
  );

  // Board view: fetch all jobs (no pagination)
  const { data: boardJobsData, isLoading: boardLoading } = useQuery<{
    jobs: Job[];
  }>(
    ["jobs-board", debouncedSearch, filters],
    async () => {
      const params: Record<string, string | number> = {
        q: debouncedSearch,
        pageSize: 9999,
        ...filters,
      };
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });
      const response = (await recruitingAPI.getJobs(params)) as JobsResponse;
      return { jobs: response.data || [] };
    },
    { enabled: viewMode === "board", staleTime: 30000 },
  );

  const boardJobs = boardJobsData?.jobs || [];

  // Board mutations
  const boardStatusMutation = useMutation(
    async ({
      id,
      status,
      note,
    }: {
      id: number;
      status: string;
      note: string;
    }) => {
      await recruitingAPI.updateJobStatus(id, { status, note });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs-board");
        queryClient.invalidateQueries("jobs");
      },
      onError: () => {
        queryClient.invalidateQueries("jobs-board");
        alert(
          t(
            "jobs.errors.statusChangeFailed",
            "Failed to update job status. Please try again.",
          ),
        );
      },
    },
  );

  const boardArchiveMutation = useMutation(
    async ({
      id,
      data,
    }: {
      id: number;
      data: {
        archive_reason: string;
        archive_reason_note: string | null;
        note?: string;
      };
    }) => {
      await recruitingAPI.archiveJob(id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs-board");
        queryClient.invalidateQueries("jobs");
      },
      onError: () => {
        queryClient.invalidateQueries("jobs-board");
        alert(
          t(
            "jobs.errors.archiveFailed",
            "Failed to archive job. Please try again.",
          ),
        );
      },
    },
  );

  const handleFilterChange = (
    filterName: keyof JobFilters,
    value: string,
  ): void => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const getEmptyMessage = (): string => {
    if (useAISearch && debouncedSearch) {
      return t(
        "jobs.emptyStates.noAIResults",
        "No jobs matched your AI search. Try different keywords or disable AI search.",
      );
    }
    if (debouncedSearch || Object.values(filters).some(Boolean)) {
      return t("jobs.emptyStates.tryAdjusting");
    }
    return t("jobs.emptyStates.getStarted");
  };

  const hasActiveFilters =
    filters.status ||
    filters.department ||
    filters.seniority ||
    filters.location ||
    filters.customer_id;

  const clearAllFilters = (): void => {
    setFilters({
      status: "",
      department: "",
      seniority: "",
      location: "",
      customer_id: "",
    });
    setCurrentPage(1);
  };

  const handleCreate = (): void => {
    setEditingJob(null);
    setShowCreateModal(true);
  };

  const handleDelete = async (
    jobId: number,
    jobTitle: string,
  ): Promise<void> => {
    if (window.confirm(t("jobs.form.confirmDelete", { title: jobTitle }))) {
      try {
        await deleteMutation.mutateAsync(jobId);
      } catch {
        alert(t("jobs.errors.deleteFailed"));
      }
    }
  };

  const handleSave = (): void => {
    setShowCreateModal(false);
    setEditingJob(null);
    queryClient.invalidateQueries("jobs");
  };

  const handleCancel = (): void => {
    setShowCreateModal(false);
    setEditingJob(null);
  };

  const handleView = (jobId: number): void => {
    navigate(`/jobs/${jobId}`);
  };

  const formatSkills = (skills: string | string[] | undefined): string[] => {
    if (!skills || (Array.isArray(skills) && skills.length === 0)) return [];
    if (Array.isArray(skills)) return skills;
    if (typeof skills === "string")
      return skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  };

  // Define job columns for table view
  const jobColumns = useMemo<TableColumn[]>(() => {
    const baseColumns: TableColumn[] = [
      {
        id: "title",
        label: t("jobs.columns.title"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "department",
        label: t("jobs.columns.department"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "owner_name",
        label: t("jobs.columns.owner"),
        render: (_value, row) => (row.owner_name as string) || "-",
      },
      {
        id: "responsible_sales_name",
        label: t("jobs.columns.responsibleSales"),
        render: (_value, row) => (row.responsible_sales_name as string) || "-",
      },
      {
        id: "responsible_recruiter_name",
        label: t("jobs.columns.responsibleRecruiter"),
        render: (_value, row) =>
          (row.responsible_recruiter_name as string) || "-",
      },
      {
        id: "location",
        label: t("jobs.columns.location"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "seniority",
        label: t("jobs.columns.seniority"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "status",
        label: t("jobs.columns.status"),
        render: (value) => {
          if (!value) return "-";
          return <JobStatusBadge status={value as string} size="small" />;
        },
      },
      {
        id: "employment_type",
        label: t("jobs.columns.employmentType"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "rates",
        label: t("jobs.columns.rates"),
        render: (_value, row) => {
          const job = row as Job;
          const rates: string[] = [];
          if (job.customer_rate)
            rates.push(
              t("jobs.columns.customerRateFormat", { rate: job.customer_rate }),
            );
          if (job.engineer_rate)
            rates.push(
              t("jobs.columns.engineerRateFormat", { rate: job.engineer_rate }),
            );
          return rates.length > 0 ? rates.join(" | ") : "-";
        },
      },
      {
        id: "created_at",
        label: t("jobs.columns.createdDate"),
        render: (value) =>
          value ? new Date(value as string).toLocaleDateString() : "-",
      },
      {
        id: "skills",
        label: t("jobs.columns.skills"),
        render: (_value, row) => {
          const job = row as Job;
          const skills = formatSkills(job.skills);
          if (skills.length === 0) return "-";
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {skills.slice(0, 3).map((skill) => (
                <Chip
                  key={`${job.id}-${skill}`}
                  label={skill}
                  size="small"
                  variant="outlined"
                />
              ))}
              {skills.length > 3 && (
                <Chip
                  label={`+${skills.length - 3}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          );
        },
      },
    ];

    // Add customer column if agency mode is enabled
    if (isAgencyMode) {
      baseColumns.splice(6, 0, {
        id: "customer_name",
        label: t("jobs.columns.customer"),
        render: (_value, row) => {
          const job = row as Job;
          return job.customer_company_name || job.customer_name || "-";
        },
      });
    }

    return baseColumns;
  }, [isAgencyMode, t]);

  const handleRowClick = (row: TableRowData): void => {
    handleView(row.id as number);
  };

  return (
    <div className="jobs-list">
      <div className="jobs-header">
        <div className="header-content">
          <h1>{t("jobs.list.title")}</h1>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <ViewSwitcher
              viewMode={viewMode}
              onViewChange={setViewMode}
              showBoard
            />
            {isAgencyMode && (
              <a href="/customers" className="btn btn-secondary">
                <Users size={16} /> {t("jobs.list.manageCustomers")}
              </a>
            )}
            <SplitActionButton
              label={t("jobs.list.newButton")}
              onClick={handleCreate}
              menuItems={[
                {
                  label: t("jobs.list.importButton", "Import"),
                  icon: <Upload size={16} />,
                  onClick: () => navigate("/jobs/import"),
                },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        className={`search-filters${useAISearch ? " search-filters--ai-active" : ""}`}
      >
        <div className="search-section">
          <div className="search-input-group">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={
                useAISearch
                  ? t(
                      "jobs.list.aiSearchPlaceholder",
                      "Search jobs with AI (e.g. 'remote React senior role')...",
                    )
                  : t("jobs.list.searchPlaceholder")
              }
              ariaLabel={t("jobs.filters.searchJobs")}
              showSearchIcon
              showClearButton
            />
            <div className="ai-search-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={useAISearch}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setUseAISearch(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                <span className="toggle-label">
                  <Bot size={16} /> {t("jobs.list.aiSearch", "AI Search")}
                </span>
                <Tooltip
                  title={t(
                    "jobs.list.aiSearchTooltip",
                    "Search by meaning instead of keywords. Try natural language like 'remote senior DevOps role'. Filters are disabled in this mode.",
                  )}
                  placement="bottom"
                  arrow
                >
                  <span className="ai-search-info-icon">
                    <Info size={14} />
                  </span>
                </Tooltip>
              </label>
            </div>
          </div>
        </div>

        <div
          className={`filters-section${useAISearch ? " filters-disabled" : ""}`}
        >
          <div className="filter-group">
            <FilterSelect
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder={t("jobs.filters.allStatus")}
              ariaLabel={t("jobs.filters.filterByStatus")}
              options={Object.entries(STATUS_CONFIG).map(([key, config]) => ({
                value: key,
                label: (config as { label: string }).label,
              }))}
            />

            <FilterSelect
              value={filters.department}
              onChange={(value) => handleFilterChange("department", value)}
              placeholder={t("jobs.filters.allDepartments")}
              ariaLabel={t("jobs.filters.filterByDepartment")}
              options={[
                {
                  value: "Engineering",
                  label: t("jobs.departments.engineering"),
                },
                { value: "Sales", label: t("jobs.departments.sales") },
                { value: "Marketing", label: t("jobs.departments.marketing") },
                {
                  value: "Operations",
                  label: t("jobs.departments.operations"),
                },
                { value: "Finance", label: t("jobs.departments.finance") },
              ]}
            />

            <FilterSelect
              value={filters.seniority}
              onChange={(value) => handleFilterChange("seniority", value)}
              placeholder={t("jobs.filters.allSeniority")}
              ariaLabel={t("jobs.filters.filterBySeniority")}
              options={[
                { value: "Junior", label: t("jobs.seniority.junior") },
                { value: "Mid", label: t("jobs.seniority.mid") },
                { value: "Senior", label: t("jobs.seniority.senior") },
                { value: "Lead", label: t("jobs.seniority.lead") },
                { value: "Principal", label: t("jobs.seniority.principal") },
              ]}
            />

            <input
              type="text"
              className={`filter-select ${filters.location ? "filter-active" : ""}`}
              placeholder={t("jobs.filters.locationPlaceholder")}
              value={filters.location}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleFilterChange("location", e.target.value)
              }
              aria-label={t("jobs.filters.filterByLocation")}
            />

            {isAgencyMode && (
              <FilterSelect
                value={filters.customer_id}
                onChange={(value) => handleFilterChange("customer_id", value)}
                placeholder={t("jobs.filters.allCustomers")}
                ariaLabel={t("jobs.filters.filterByCustomer")}
                options={customers.map((customer) => ({
                  value: String(customer.id),
                  label: customer.company_name || customer.name || "",
                }))}
              />
            )}

            <ClearFiltersButton
              visible={!!hasActiveFilters}
              onClick={clearAllFilters}
            />
          </div>
        </div>
      </div>

      {/* Jobs Grid or Table */}
      {(() => {
        if (isLoading) {
          return <LoadingState message={t("common:loading.loading")} />;
        }
        if (error) {
          return (
            <ErrorState
              title={t("jobs.errors.loadingJobs")}
              message={
                (error as Error).message || t("jobs.errors.loadingJobsGeneric")
              }
            />
          );
        }
        if (viewMode === "board") {
          return (
            <StatusKanbanBoard
              items={boardJobs as (Job & { status: string })[]}
              columns={JOB_KANBAN_COLUMNS}
              statusConfig={JOB_STATUS_CONFIG}
              hiddenByDefault={JOB_BOARD_HIDDEN_BY_DEFAULT}
              archiveColumnKey="archived"
              archiveReasons={JOB_ARCHIVE_REASONS}
              entityName="Job"
              validTransitions={null}
              onStatusChange={async (itemId, newStatus, note) => {
                await boardStatusMutation.mutateAsync({
                  id: itemId,
                  status: newStatus,
                  note,
                });
              }}
              onArchive={async (itemId, data) => {
                await boardArchiveMutation.mutateAsync({ id: itemId, data });
              }}
              renderCard={(job, isDragOverlay) => (
                <JobBoardCard
                  key={job.id}
                  job={job}
                  onNavigate={(id) => navigate(`/jobs/${id}`)}
                  isDragOverlay={isDragOverlay}
                />
              )}
              isLoading={boardLoading}
              persistKey="jobs-board-show-completed"
            />
          );
        }
        if (jobs.length === 0) {
          return (
            <EmptyState
              title={t("jobs.emptyStates.noJobs")}
              message={getEmptyMessage()}
            />
          );
        }
        if (viewMode === "table") {
          return (
            <>
              <TableView
                columns={jobColumns}
                data={jobs}
                loading={isLoading}
                onRowClick={handleRowClick}
                searchable={false}
                filterable={true}
                selectable={false}
              />
              {/* Pagination */}
              {!useAISearch && jobs.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  hasMore={jobs.length >= 20}
                />
              )}
            </>
          );
        }
        return (
          <>
            <div className="jobs-grid">
              {jobs.map((job) => {
                const skills = formatSkills(job.skills);
                const displaySkills = skills;

                return (
                  <div
                    key={job.id}
                    className="job-card"
                    onClick={() => handleView(job.id)}
                  >
                    {/* Header: Badges row + Title */}
                    <div className="job-header">
                      <div className="job-title-section">
                        <div className="job-badges-row">
                          {job.similarity_score != null && (
                            <span className="similarity-badge">
                              {Math.round(job.similarity_score * 100)}% match
                            </span>
                          )}
                          {job.status && <JobStatusDropdown job={job} />}
                          <AppCountBadge
                            count={job.application_count ?? 0}
                            mostAdvancedStatus={job.most_advanced_app_status}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/jobs/${job.id}?tab=applications`);
                            }}
                          />
                        </div>
                        <h3>
                          {job.title}
                          {(job as Job & { _unindexed?: boolean })
                            ._unindexed && (
                            <span
                              className="unindexed-badge"
                              role="presentation"
                              onClickCapture={(e) => e.stopPropagation()}
                            >
                              {t("jobs.list.notAiIndexed", "Not AI-indexed")}
                            </span>
                          )}
                        </h3>
                      </div>
                    </div>

                    {/* Meta row: Location, Seniority, Type */}
                    <div className="job-meta-row">
                      {job.location && (
                        <span className="meta-item">
                          <MapPin size={14} /> {job.location}
                        </span>
                      )}
                      {job.seniority && (
                        <span className="meta-item">
                          <Briefcase size={14} /> {job.seniority}
                        </span>
                      )}
                      {job.employment_type && (
                        <span className="meta-item">
                          <Clock size={14} /> {job.employment_type}
                        </span>
                      )}
                    </div>

                    {/* Description preview */}
                    {job.description && (
                      <div className="job-description">
                        <p className="job-description-text">
                          {job.description.substring(0, 300)}
                        </p>
                      </div>
                    )}

                    {/* Skills tags */}
                    {skills.length > 0 && (
                      <div className="job-skills">
                        {displaySkills.map((skill) => (
                          <span
                            key={`card-${job.id}-${skill}`}
                            className="skill-tag"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Footer: Owner + Personnel + Calendar + Sourcing */}
                    <div className="job-footer-meta">
                      <div className="job-footer-left">
                        <span
                          className="personnel-label"
                          title={`Created by: ${job.owner_name || t("jobs.list.unassigned")}`}
                        >
                          Created by:{" "}
                          {job.owner_name || t("jobs.list.unassigned")}
                        </span>
                        {job.responsible_sales_name && (
                          <span
                            className="personnel-label"
                            title={`KAM: ${job.responsible_sales_name}`}
                          >
                            KAM: {job.responsible_sales_name}
                          </span>
                        )}
                        {job.responsible_recruiter_name && (
                          <span
                            className="personnel-label"
                            title={`Recruiter: ${job.responsible_recruiter_name}`}
                          >
                            Recruiter: {job.responsible_recruiter_name}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        {(job.status === "find_candidates_passive" ||
                          job.status === "find_candidates_active") && (
                          <button
                            className="calendar-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/jobs/${job.id}?tab=sourcing`);
                            }}
                            aria-label="View Sourcing"
                            title="AI Sourcing"
                          >
                            <Search size={14} />
                          </button>
                        )}
                        <button
                          className="calendar-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCalendarAnchor({ el: e.currentTarget, job });
                          }}
                          aria-label={t("jobs.list.viewDates", "View dates")}
                        >
                          <Calendar size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Calendar Popover */}
            <Popover
              open={Boolean(calendarAnchor)}
              anchorEl={calendarAnchor?.el}
              onClose={() => setCalendarAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              onClick={(e) => e.stopPropagation()}
            >
              {calendarAnchor && (
                <div className="calendar-popover">
                  <div className="calendar-popover-row">
                    <span className="calendar-popover-label">
                      {t("jobs.list.created", "Created")}
                    </span>
                    <span>
                      {calendarAnchor.job.created_at
                        ? new Date(
                            calendarAnchor.job.created_at,
                          ).toLocaleDateString()
                        : "-"}
                    </span>
                  </div>
                  <div className="calendar-popover-row">
                    <span className="calendar-popover-label">
                      {t("jobs.list.lastActivity", "Last activity")}
                    </span>
                    <span>
                      {calendarAnchor.job.updated_at
                        ? new Date(
                            calendarAnchor.job.updated_at,
                          ).toLocaleDateString()
                        : "-"}
                    </span>
                  </div>
                </div>
              )}
            </Popover>

            {/* Pagination */}
            {!useAISearch && jobs.length > 0 && (
              <Pagination
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                hasMore={jobs.length >= 20}
              />
            )}
          </>
        );
      })()}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={handleCancel}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") handleCancel();
          }}
          role="presentation"
        >
          <div
            className="modal job-modal-wide"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <JobForm
              job={editingJob}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={async (jobId: number) => {
                await handleDelete(jobId, editingJob ? editingJob.title : "");
                handleCancel();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsList;
